import axios from 'axios';
import { logoutUser } from './dataService';

// IMPORTANT: Update this URL to match your backend server address
const API_URL = 'https://apienglish.nguyenluan.vn/api/chatbot';

/**
 * A centralized error handler for chatbot API calls.
 * It checks for 401 Unauthorized errors and logs the user out.
 * @param {Error} error - The error object from axios.
 * @param {string} defaultMessage - A default error message to use.
 */
const handleApiError = (error, defaultMessage) => {
    console.error(`Chatbot API Error:`, error.response ? error.response.data : error.message);
    if (error.response && error.response.status === 401) {
        logoutUser(); // This will remove token and reload the page
        // Throwing an error here is good practice, although the page reload will interrupt it.
        throw new Error('Token không hợp lệ hoặc đã hết hạn. Đang đăng xuất...');
    }
    const errorMessage = error.response?.data?.error || error.message || defaultMessage;
    throw new Error(errorMessage);
};

/**
 * Gets the authorization header with the JWT token.
 * If no token is found, it logs the user out.
 * @returns {Object} The axios config object with Authorization header.
 */
const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        console.error('Lỗi xác thực: Không tìm thấy token.');
        logoutUser(); // Force logout and reload if token is missing
        throw new Error('Yêu cầu xác thực. Vui lòng đăng nhập lại.');
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };
};

/**
 * Sends a message to the chatbot backend and gets a response.
 * @param {Object} payload - The data to send to the API.
 * @returns {Promise<Object>} The API response data.
 */
const sendMessage = async (payload) => {
    try {
        const config = getAuthConfig();
        const response = await axios.post(`${API_URL}/chat`, payload, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Gửi tin nhắn thất bại.');
    }
};

/**
 * Sends a message to the chatbot backend and gets a streaming response.
 * @param {Object} payload - The data to send to the API.
 * @param {Function} onChunk - Callback function for each text chunk.
 * @returns {Promise<Object>} The complete response data.
 */
const sendStreamingMessage = async (payload, onChunk) => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            logoutUser();
            throw new Error('Yêu cầu xác thực. Vui lòng đăng nhập lại.');
        }
        const response = await fetch(`${API_URL}/stream`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            if (response.status === 401) {
                logoutUser();
                throw new Error('Token không hợp lệ hoặc đã hết hạn. Đang đăng xuất...');
            }
            throw new Error('Gửi tin nhắn thất bại.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';

        while (true) {
            const { done, value } = await reader.read();
            
            if (done) break;
            
            const chunk = decoder.decode(value, { stream: true });
            
            // Check for completion first
            if (chunk.includes('[DONE]')) {
                // Process any remaining content before [DONE]
                const content = chunk.split('[DONE]')[0];
                if (content) {
                    fullResponse += content;
                    onChunk(content);
                }
                break;
            }
            
            if (chunk.includes('[ERROR]')) {
                const errorMatch = chunk.match(/\[ERROR\]:\s*(.+)/);
                if (errorMatch) {
                    throw new Error(errorMatch[1].trim());
                }
            }
            
            // Send the chunk directly to UI for immediate display
            if (chunk && !chunk.includes('[DONE]') && !chunk.includes('[ERROR]')) {
                fullResponse += chunk;
                onChunk(chunk);
            }
        }

        return {
            response: fullResponse.trim(),
            conversationId: payload.conversationId
        };

    } catch (error) {
        throw new Error(error.message || 'Gửi tin nhắn streaming thất bại.');
    }
};

/**
 * Fetches the list of conversations for the current user.
 * @returns {Promise<Array>} A list of conversations.
 */
const getConversations = async () => {
    try {
        const config = getAuthConfig();
        const response = await axios.get(`${API_URL}/conversations`, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể tải lịch sử trò chuyện.');
    }
};

/**
 * Fetches all messages for a specific conversation.
 * @param {string} conversationId - The ID of the conversation.
 * @returns {Promise<Array>} A list of messages.
 */
const getConversationMessages = async (conversationId) => {
    try {
        const config = getAuthConfig();
        const response = await axios.get(`${API_URL}/conversations/${conversationId}`, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể tải tin nhắn trong cuộc trò chuyện.');
    }
};

/**
 * Creates a new conversation with a given topic.
 * @param {string} topic - The topic for the new conversation.
 * @returns {Promise<Object>} The newly created conversation object.
 */
const createConversation = async (topic) => {
    try {
        const config = getAuthConfig();
        const response = await axios.post(`${API_URL}/conversations`, { topic }, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể tạo cuộc trò chuyện mới.');
    }
};

const chatbotService = {
    sendMessage,
    sendStreamingMessage,
    getConversations,
    getConversationMessages,
    createConversation,
};

export default chatbotService;
